import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'

export interface VeilProps {
  /** What the cover says before anything is revealed. */
  label?: string
  /** Rendered only while revealed - never present in the DOM otherwise. */
  children: ReactNode
  tone?: 'neutral' | 'liberal' | 'fascist'
  /**
   * Reserves the height the revealed secret will need, so the buttons below
   * the veil do not jump out from under a thumb the instant a card appears.
   */
  frame?: 'text' | 'hand' | 'card' | 'emblem'
  /**
   * Whether the skin supplies artwork for this secret. A role card roughly
   * doubles the height of what is behind the cover, so the space reserved has
   * to know: reserving for artwork on a skin that ships none left a third of
   * the reveal screen empty and pushed it into scrolling.
   */
  art?: boolean
  /**
   * The card frame again, but carrying a list of names under the role rather
   * than a line of text. Same reason as `art`: the cover has to reserve what
   * the content will need, and a fascist's team is most of a screen taller than
   * a liberal's "you know nothing about anyone else".
   */
  team?: boolean
  /** Called each time the secret is shown, so a screen can tell it has been read. */
  onReveal?: () => void
  testId?: string
}

/**
 * How long the secret stays up after the finger lifts. Long enough that a
 * hold which ends a beat before the reader has finished the last line does
 * not snatch it away, short enough that the screen is covered again before
 * the phone is back on the table.
 */
export const VEIL_LINGER_MS = 600

/**
 * Press and hold to reveal a secret you only have to *read* - your own role,
 * an investigation, the three cards a Policy Peek shows. A secret you have to
 * act on is not this control: see `PolicyChoice`, where the cards themselves
 * are the buttons and showing them is a toggle, because a hold that runs out
 * mid-decision is a hand you have to re-open to aim with.
 *
 * Everything about this component exists to stop a secret sitting exposed on a
 * screen at a table:
 *
 *  - the secret is conditionally rendered, so when hidden it is not in the DOM
 *    at all (a blur or an opacity cover would still be there to be read)
 *  - release is detected on the window, so lifting a finger anywhere hides it,
 *    after a short linger (`VEIL_LINGER_MS`)
 *  - it hides at once, with no linger, on tab switch, window blur, page hide and screen lock
 *  - nothing under the finger can be selected, dragged or called out: a phone
 *    reads a press and hold over text as "select this", and a selection
 *    outlives the release that was supposed to end the peek
 *
 * The cover reserves the height of what it hides, so revealing a secret does
 * not shove whatever is under it down the screen mid-press.
 */
export function Veil({
  label = 'Hold to reveal',
  children,
  tone = 'neutral',
  frame = 'text',
  art = false,
  team = false,
  onReveal,
  testId,
}: VeilProps) {
  const [revealed, setRevealed] = useState(false)
  const linger = useRef<ReturnType<typeof setTimeout> | null>(null)
  const onRevealRef = useRef(onReveal)
  onRevealRef.current = onReveal

  const settle = useCallback(() => {
    if (linger.current !== null) {
      clearTimeout(linger.current)
      linger.current = null
    }
  }, [])
  /** Covered now. Everything that is not a finger lifting comes through here. */
  const hide = useCallback(() => {
    settle()
    setRevealed(false)
  }, [settle])
  const hold = useCallback(() => {
    // Pressing again inside the linger keeps the secret up rather than
    // letting the pending cover drop on a finger that is back on the glass.
    settle()
    setRevealed(true)
    onRevealRef.current?.()
  }, [settle])
  /** Finger lifted. The secret follows it a moment later. */
  const release = useCallback(() => {
    settle()
    linger.current = setTimeout(() => {
      linger.current = null
      setRevealed(false)
    }, VEIL_LINGER_MS)
  }, [settle])

  useEffect(() => settle, [settle])

  useEffect(() => {
    if (!revealed) return
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') hide()
    }
    // The press and hold this control is made of is, to a phone, the gesture
    // that selects a word or picks up an image. CSS says no to both; these say
    // it again at the moment of the gesture, for the browsers that start one
    // anyway - and only for as long as a secret is actually on the screen.
    const stop = (e: Event) => e.preventDefault()
    // Release is tracked on the window: the finger may leave the element, and
    // on a phone it very often does.
    window.addEventListener('pointerup', release)
    window.addEventListener('pointercancel', hide)
    window.addEventListener('blur', hide)
    window.addEventListener('pagehide', hide)
    document.addEventListener('visibilitychange', onVisibility)
    document.addEventListener('selectstart', stop)
    document.addEventListener('dragstart', stop)
    return () => {
      window.removeEventListener('pointerup', release)
      window.removeEventListener('pointercancel', hide)
      window.removeEventListener('blur', hide)
      window.removeEventListener('pagehide', hide)
      document.removeEventListener('visibilitychange', onVisibility)
      document.removeEventListener('selectstart', stop)
      document.removeEventListener('dragstart', stop)
    }
  }, [revealed, release, hide])

  return (
    <div
      className={`veil veil--${tone} veil--frame-${frame} ${art ? 'has-art' : ''} ${
        team ? 'has-team' : ''
      } ${revealed ? 'is-revealed' : ''}`}
      data-testid={testId}
      data-revealed={revealed ? 'true' : 'false'}
    >
      <button
        type="button"
        className="veil__surface"
        data-testid={testId ? `${testId}-cover` : undefined}
        aria-label={label}
        aria-pressed={revealed}
        onPointerDown={(e) => {
          // Cancelling the press stops the browser taking it for the start of
          // a selection or a drag before anything of ours has run. It costs
          // the button its focus-on-click, which is handed back by hand so
          // that a click and then a space bar still works.
          e.preventDefault()
          e.currentTarget.focus()
          hold()
        }}
        onContextMenu={(e) => e.preventDefault()}
        onKeyDown={(e) => {
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault()
            hold()
          }
        }}
        onKeyUp={(e) => {
          if (e.key === ' ' || e.key === 'Enter') release()
        }}
      >
        {revealed ? (
          <span className="veil__content" data-testid={testId ? `${testId}-content` : undefined}>
            {children}
          </span>
        ) : (
          <span className="veil__cover">
            <span className="veil__lock" aria-hidden="true">
              &#9679;&#9679;&#9679;
            </span>
            <span className="veil__label">{label}</span>
          </span>
        )}
      </button>
    </div>
  )
}
